/**
 * Jotai atoms for Entity Verification feature state management
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { ClinicalNotesAnalysis } from '../../contracts';
import { entityVerificationDrawerOpenAtom } from './drawer-open-port';

export { entityVerificationDrawerOpenAtom };

/**
 * Currently selected entity index in the verification list
 * null means no entity is selected
 */
export const selectedEntityIndexAtom = atom<number | null>(null);

/**
 * Currently selected hallucination index in the additional information list
 * null means no hallucination is selected
 */
export const selectedHallucinationIndexAtom = atom<number | null>(null);

/**
 * Entity index that the user navigated to via "View in note".
 * When showVerifiedHighlights is off, only this entity (if set) gets a verified highlight in the note.
 * Cleared when the drawer is closed.
 */
export const highlightInNoteEntityIndexAtom = atom<number | null>(null);

/**
 * Hallucination index that the user navigated to via "View in note".
 * When showIssueHighlights is off, only this hallucination gets a highlight.
 */
export const highlightInNoteHallucinationIndexAtom = atom<number | null>(null);

/**
 * Drawer width - persisted to localStorage
 * Default: 380px to match Tasks drawer, Min: 320px, Max: 600px
 */
export const drawerWidthAtom = atomWithStorage<number>(
  'entity-verification-drawer-width',
  380
);

/**
 * Currently active analysis data (from API or cache)
 */
export const currentAnalysisAtom = atom<ClinicalNotesAnalysis | null>(null);

/**
 * Whether an analysis is currently being generated, keyed by session ID.
 * Session-scoped so navigating away and back preserves the loading state.
 */
export const isAnalyzingBySessionAtom = atom<Record<string, boolean>>({});

/**
 * Active tab in the analysis drawer
 * 'issues' = Potential Issues tab (hallucinations + missing entities)
 * 'entities' = All Entities tab (verified entities)
 */
export type AnalysisDrawerTab = 'issues' | 'entities';
export const activeAnalysisTabAtom = atom<AnalysisDrawerTab>('issues');

/**
 * Visibility toggles for inline highlights - persisted to localStorage
 */
export const showVerifiedHighlightsAtom = atomWithStorage<boolean>(
  'entity-verification-show-verified',
  false
);

export const showIssueHighlightsAtom = atomWithStorage<boolean>(
  'entity-verification-show-issues',
  true
);

/**
 * Action atom to select an entity and switch to the correct drawer tab.
 * @param index - Entity index to select, or null to clear.
 * @param drawerTab - Which drawer tab to show ('issues' for missing/inaccurate, 'entities' for verified list). Defaults to 'entities'.
 */
export const selectEntityWithTabAtom = atom(
  null,
  (get, set, index: number | null, drawerTab: AnalysisDrawerTab = 'entities') => {
    set(selectedEntityIndexAtom, index);
    set(selectedHallucinationIndexAtom, null);
    if (index !== null) {
      set(entityVerificationDrawerOpenAtom, true);
      set(activeAnalysisTabAtom, drawerTab);
    }
  }
);

/**
 * Action atom to select a hallucination and switch to the correct tab
 */
export const selectHallucinationWithTabAtom = atom(
  null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (get, set, index: number | null, drawerTab?: AnalysisDrawerTab) => {
    set(selectedHallucinationIndexAtom, index);
    set(selectedEntityIndexAtom, null);
    if (index !== null) {
      set(entityVerificationDrawerOpenAtom, true);
      set(activeAnalysisTabAtom, 'issues');
    }
  }
);

/**
 * Tracks which hallucination indices have had their suggested fix applied.
 * Stored at feature level (not component) so it survives tab switches between Issues and All.
 */
export const appliedHallucinationFixIndicesAtom = atom<Set<number>>(new Set<number>());

/**
 * Tracks which entity (inaccurate) indices have had their suggested fix applied.
 * Stored at feature level (not component) so it survives tab switches between Issues and All.
 */
export const appliedEntityFixIndicesAtom = atom<Set<number>>(new Set<number>());

/**
 * When true, decoration rebuilds are suppressed so fix animations
 * can play without the DOM span being destroyed mid-animation.
 */
export const decorationRebuildLockedAtom = atom(false);

// ---------------------------------------------------------------------------
// Auto-mode (Heidi Verify runs automatically alongside note generation)
// ---------------------------------------------------------------------------

export type AutoVerifyGenerationLifecycle =
  | { phase: 'starting'; analysisId?: undefined }
  | { phase: 'in_flight'; analysisId: string };

/**
 * Shared auto-verify ownership keyed by session. An entry exists only while a
 * generation's pipeline is owned and unresolved: `starting` while the start
 * request is in flight, `in_flight` once the backend analysis identity is
 * registered. Terminal or superseded analyses clear the entry (see
 * `resolveAutoVerifyGenerationAtom`), so no entry means the session's active
 * generation is claimable.
 *
 * Entries are bounded by the sessions visited in a page load (one small
 * record each) and are never evicted — a terminal marker is deliberately not
 * retained because no consumer distinguishes it from an absent entry.
 */
const autoVerifyGenerationLifecycleBaseAtom = atom<
  Record<string, AutoVerifyGenerationLifecycle>
>({});

/** Read-only view; all writes go through the guarded atoms below. */
export const autoVerifyGenerationLifecycleAtom = atom((get) =>
  get(autoVerifyGenerationLifecycleBaseAtom)
);

/**
 * Last resolved note-generation state observed for each session, shared across
 * hook mounts so a generation transition is detected exactly once.
 */
const lastObservedNoteGeneratingAtom = atom<Record<string, boolean>>({});

/**
 * Records the note-generation state a mounted hook observed. A resolved
 * false → true transition means a new generation superseded the previous one,
 * so any registered lifecycle entry is dropped to let the new generation
 * claim — even when the previous analysis was never observed reaching
 * terminal (e.g. superseded by a manual analysis or another client).
 * A `starting` entry is kept: its in-flight start request owns the claim and
 * exits via register or release.
 *
 * Accepted boundary: the transition needs a mounted hook to observe the
 * not-generating gap. If no surface is mounted across the gap, a stuck
 * `in_flight` entry survives until the watcher observes a superseding *auto*
 * analysis reach terminal (the watcher never observes manual analyses, so a
 * manual supersession needs the next mounted generation transition) or the
 * page reloads.
 */
export const observeNoteGenerationAtom = atom(
  null,
  (
    get,
    set,
    input: {
      sessionId: string;
      isGenerating: boolean;
      isStreamingStatusResolved: boolean;
    }
  ) => {
    // Unresolved status is assumed-generating for UI safety and says nothing
    // about generation identity — never treat it as a transition.
    if (!input.isStreamingStatusResolved) return;

    const observed = get(lastObservedNoteGeneratingAtom);
    const wasGenerating = observed[input.sessionId] ?? false;
    if (wasGenerating === input.isGenerating) return;

    set(lastObservedNoteGeneratingAtom, {
      ...observed,
      [input.sessionId]: input.isGenerating,
    });
    if (wasGenerating || !input.isGenerating) return;

    const lifecycles = get(autoVerifyGenerationLifecycleBaseAtom);
    const current = lifecycles[input.sessionId];
    if (!current || current.phase === 'starting') return;

    const remaining = { ...lifecycles };
    delete remaining[input.sessionId];
    set(autoVerifyGenerationLifecycleBaseAtom, remaining);
  }
);

/**
 * Atomically claims the active note generation for a session. Returns true to
 * a single live claimant. No entry means claimable — entries are dropped
 * (becoming re-claimable) when a start fails or times out (release), when a
 * resolved generation transition rotates a non-`starting` entry, or when the
 * registered analysis terminates or is superseded (resolve). Requires
 * resolved streaming status: an unresolved "generating" is a UI-safety
 * assumption, not evidence a generation is running.
 */
export const claimAutoVerifyGenerationAtom = atom(
  null,
  (
    get,
    set,
    input: { sessionId: string; isStreamingStatusResolved: boolean }
  ): boolean => {
    if (!input.isStreamingStatusResolved) return false;

    const lifecycles = get(autoVerifyGenerationLifecycleBaseAtom);
    if (lifecycles[input.sessionId]) return false;

    set(autoVerifyGenerationLifecycleBaseAtom, {
      ...lifecycles,
      [input.sessionId]: { phase: 'starting' },
    });
    return true;
  }
);

/** Associates the backend analysis identity with the claimed generation. */
export const registerAutoVerifyAnalysisAtom = atom(
  null,
  (get, set, input: { sessionId: string; analysisId: string }) => {
    const lifecycles = get(autoVerifyGenerationLifecycleBaseAtom);
    if (lifecycles[input.sessionId]?.phase !== 'starting') return;

    set(autoVerifyGenerationLifecycleBaseAtom, {
      ...lifecycles,
      [input.sessionId]: {
        phase: 'in_flight',
        analysisId: input.analysisId,
      },
    });
  }
);

/**
 * Clears the session's lifecycle entry once the generation's analysis has
 * resolved, so a later generation can claim. What the atom guarantees: only
 * an `in_flight` entry is affected, and it is always dropped — both when the
 * terminating analysis matches the registration and when a *different*
 * analysis is observed terminating (the registered analysis was superseded
 * and can never terminate).
 *
 * Caller precondition (not enforced here): `analysisId` must be a genuinely
 * observed terminal — the registered analysis itself reaching
 * completed/failed, or an observed in-flight → terminal transition. Feeding
 * a bare cached terminal result with a mismatched id would drop a live
 * registration; the query cache can hold a previous generation's completed
 * analysis, so callers must gate on identity or observed transition first
 * (`useAutoVerify`'s lifecycle guard, `SessionPoller`'s observation rules).
 */
export const resolveAutoVerifyGenerationAtom = atom(
  null,
  (get, set, input: { sessionId: string; analysisId: string }) => {
    const lifecycles = get(autoVerifyGenerationLifecycleBaseAtom);
    const current = lifecycles[input.sessionId];
    if (current?.phase !== 'in_flight') return;

    const remaining = { ...lifecycles };
    delete remaining[input.sessionId];
    set(autoVerifyGenerationLifecycleBaseAtom, remaining);
  }
);

/** Releases a starting claim after its request fails so a later retry can run. */
export const releaseAutoVerifyGenerationClaimAtom = atom(
  null,
  (get, set, sessionId: string) => {
    const lifecycles = get(autoVerifyGenerationLifecycleBaseAtom);
    if (lifecycles[sessionId]?.phase !== 'starting') return;

    const remaining = { ...lifecycles };
    delete remaining[sessionId];
    set(autoVerifyGenerationLifecycleBaseAtom, remaining);
  }
);

/**
 * Session IDs for which an auto-mode pipeline has been kicked off by this client
 * and has not yet reached a terminal state. Populated when `start_auto_pipeline`
 * returns and cleared by the global watcher on terminal status.
 *
 * Cross-session behavior: a global watcher subscribes to this set and continues
 * polling each session's analysis even after the user navigates away, so the
 * completion toast still fires.
 */
export const inFlightAutoVerifySessionsAtom = atom<Set<string>>(new Set<string>());

/**
 * Session ID for which the "Review your note" popover should be shown, anchored
 * to the Verify button in the note header. `null` when no popover is active.
 *
 * Only set when the user is still on the session where auto-verify completed.
 * Cross-session completions surface as a toast instead (see the global watcher).
 */
export const autoVerifyPopoverSessionAtom = atom<string | null>(null);

/**
 * Analysis id for which the completion popover has already been fired, keyed
 * by session. Shared across hook mounts so concurrent viewers of the same
 * session show/track the popover exactly once per analysis — a remount over
 * an already-completed analysis must not re-fire it. A new generation mints
 * a new analysis id, so no reset is needed. One entry per visited session,
 * overwritten each generation; never evicted within a page load.
 */
const autoVerifyPopoverFiredBaseAtom = atom<Record<string, string>>({});

/**
 * Atomically claims the popover for an analysis. Returns true to the first
 * caller for that (session, analysis id) pair; every later caller — a
 * concurrent mount or a remount — gets false.
 */
export const claimAutoVerifyPopoverAtom = atom(
  null,
  (get, set, input: { sessionId: string; analysisId: string }): boolean => {
    const fired = get(autoVerifyPopoverFiredBaseAtom);
    if (fired[input.sessionId] === input.analysisId) return false;

    set(autoVerifyPopoverFiredBaseAtom, {
      ...fired,
      [input.sessionId]: input.analysisId,
    });
    return true;
  }
);
